import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
type PolicyKey = 'privacy' | 'terms' | 'refund' | null;
const policies: Record<Exclude<PolicyKey, null>, {
  title: string;
  content: string[];
}> = {
  privacy: {
    title: 'Política de Privacidad',
    content: ['En VitalyForms, respetamos y protegemos tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.', 'Información que recopilamos: Cuando realizas una compra, recopilamos tu nombre, dirección de correo electrónico e información de pago. Los pagos son procesados de forma segura por Stripe o PayPal — nunca almacenamos los datos de tu tarjeta de crédito.', 'Uso de la información: Tu correo electrónico se utiliza únicamente para enviarte el producto adquirido y comunicaciones relacionadas con tu compra. No vendemos, alquilamos ni compartimos tu información personal con terceros.', 'Cookies: Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio de forma anónima.', 'Seguridad: Utilizamos cifrado SSL y medidas de seguridad estándar de la industria para proteger tu información personal.', 'Contacto: Si tienes preguntas sobre nuestra política de privacidad, puedes escribirnos a contacto@vitalyforms.com.']
  },
  terms: {
    title: 'Términos de Servicio',
    content: ['Al comprar y descargar "El Fuego que Nadie Ve", aceptas los siguientes términos y condiciones.', 'Producto digital: Este es un producto digital en formato PDF. Una vez completado el pago, recibirás acceso inmediato para descargar el manual. No se realizan envíos físicos.', 'Uso personal: El contenido de este manual es para tu uso personal únicamente. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa de VitalyForms.', 'Propósito educativo: Este producto es para propósitos educativos e informativos únicamente. No constituye consejo médico profesional. Consulta siempre a tu médico antes de realizar cambios en tu dieta o estilo de vida.', 'Resultados: Los resultados individuales pueden variar. Los testimonios presentados reflejan experiencias individuales y no garantizan resultados específicos.', 'Modificaciones: VitalyForms se reserva el derecho de modificar estos términos en cualquier momento. Las modificaciones serán efectivas desde su publicación en el sitio web.']
  },
  refund: {
    title: 'Política de Reembolso',
    content: ['Tu satisfacción es nuestra prioridad. Ofrecemos una garantía de devolución de dinero de 30 días sin preguntas.', 'Cómo solicitar un reembolso: Si después de seguir el protocolo completo durante 7 días no estás satisfecha con los resultados, simplemente envía un correo electrónico a reembolsos@vitalyforms.com dentro de los 30 días posteriores a tu compra.', 'Proceso: Una vez recibida tu solicitud, procesaremos tu reembolso completo dentro de 5-7 días hábiles. El reembolso se realizará al mismo método de pago utilizado en la compra original.', 'Sin preguntas: No requerimos formularios complicados, justificaciones ni devolución del producto digital. Creemos en nuestro protocolo y queremos que tu experiencia sea libre de riesgos.', 'Contacto: Para cualquier consulta relacionada con reembolsos, escríbenos a reembolsos@vitalyforms.com.']
  }
};
export function Footer() {
  const [openPolicy, setOpenPolicy] = useState<PolicyKey>(null);
  const togglePolicy = (key: Exclude<PolicyKey, null>) => {
    setOpenPolicy(openPolicy === key ? null : key);
  };
  return <footer className="bg-[#2F4F4F] py-12 px-6 md:px-12 lg:px-24 text-center text-sm" data-id="element-50">
      <div className="max-w-4xl mx-auto space-y-6" data-id="element-51">
        <p className="text-white font-medium" data-id="element-52">
          © 2026 VitalyForms. Todos los derechos reservados. VitalyForms.com
        </p>

        <p className="leading-relaxed max-w-3xl mx-auto text-white/80" data-id="element-53">
          Este producto es para propósitos educativos únicamente. No reemplaza
          el consejo médico profesional. Los resultados individuales pueden
          variar.
        </p>

        <div className="flex flex-wrap justify-center gap-4 md:gap-8 pt-4 border-t border-[#C9A84C]/30" data-id="element-54">
          {(['privacy', 'terms', 'refund'] as const).map((key, index) => <Fragment key={key} data-id="element-55">
              {index > 0 && <span className="hidden md:inline text-[#C9A84C]" data-id="element-56">•</span>}
              <button onClick={() => togglePolicy(key)} className="text-[#C9A84C] hover:text-[#e0c06a] transition-colors flex items-center gap-1" data-id="element-57">
                {policies[key].title}
                <ChevronDown size={14} className={`transition-transform duration-300 ${openPolicy === key ? 'rotate-180' : ''}`} data-id="element-58" />
              </button>
            </Fragment>)}
        </div>

        <AnimatePresence data-id="element-59">
          {openPolicy && <motion.div key={openPolicy} initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-60">
              <div className="bg-white/5 rounded-xl p-6 md:p-8 text-left border border-[#C9A84C]/30 mt-4" data-id="element-61">
                <h3 className="text-[#C9A84C] font-bold text-lg mb-4 text-center" data-id="element-62">
                  {policies[openPolicy].title}
                </h3>
                <div className="space-y-4" data-id="element-63">
                  {policies[openPolicy].content.map((paragraph, i) => <p key={i} className="text-white/80 leading-relaxed text-sm" data-id="element-64">
                      {paragraph}
                    </p>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </footer>;
}